import {
    AdvancedFilterGroupNode,
    AdvancedFilterRelasjonNode,
    appendAdvancedFilterNodeToClause,
    removeAdvancedFilterNode,
    resolveAdvancedFilterRelationTargetOptions,
    replaceAdvancedFilterNode,
    updateAdvancedFilterClauseNegated,
    updateAdvancedFilterGroupNegated,
} from '@/domain/advancedFilter';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { VegobjekttypeState } from '@/state';
import { UserState } from '@/state/UserState';
import * as React from 'react';
import { AddButtons, createNodeForAddAction } from './AdvancedFilterAddButtons';
import { AdvancedFilterGroupEditor } from './AdvancedFilterGroupEditor';

interface Props {
    root: AdvancedFilterGroupNode;
    node: AdvancedFilterRelasjonNode;
    nodeDepth: number;
    vegobjekttypeData: VegobjekttypeState;
    datakatalogVegobjekttyper: Vegobjekttype[];
    errorsByNodeId: Record<string, string[]>;
    onDraftChange: (draft: AdvancedFilterGroupNode | null) => void;
    user: UserState;
    disableSuggestedValueFetch?: boolean;
}

export const RelasjonClauseEditor: React.FC<Props> = ({
    root,
    node,
    nodeDepth,
    vegobjekttypeData,
    datakatalogVegobjekttyper,
    errorsByNodeId,
    onDraftChange,
    user,
    disableSuggestedValueFetch = false,
}) => {
    const relationTargetOptions = React.useMemo(
        () =>
            resolveAdvancedFilterRelationTargetOptions(
                root,
                node.id,
                datakatalogVegobjekttyper,
                vegobjekttypeData.typeId
            ),
        [datakatalogVegobjekttyper, node.id, root, vegobjekttypeData.typeId]
    );
    const selectedOption =
        relationTargetOptions.find(option => option.targetTypeId === node.targetTypeId) ??
        null;

    const selectedType = node.targetTypeId
        ? (datakatalogVegobjekttyper.find(type => type.id === node.targetTypeId) ?? null)
        : null;

    const handleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextValue = event.target.value;
        const targetTypeId = nextValue ? Number.parseInt(nextValue, 10) : null;
        onDraftChange(
            replaceAdvancedFilterNode(root, node.id, currentNode => ({
                ...currentNode,
                targetTypeId,
                child: targetTypeId === node.targetTypeId ? node.child : null,
            }))
        );
    };

    const handleNegationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const negated = event.target.value === 'without';
        onDraftChange(updateAdvancedFilterClauseNegated(root, node.id, negated));
    };

    const handleChildNegationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!node.child) {
            return;
        }

        const negated = event.target.value === 'without';
        onDraftChange(updateAdvancedFilterGroupNegated(root, node.child.id, negated));
    };

    const handleRemoveChild = () => {
        if (!node.child) {
            return;
        }
        onDraftChange(removeAdvancedFilterNode(root, node.child.id));
    };

    const childContainerDepth = nodeDepth + 1;
    const handleAddNestedNode = React.useCallback(
        (kind: Parameters<typeof createNodeForAddAction>[0]) => {
            onDraftChange(
                appendAdvancedFilterNodeToClause(
                    root,
                    node.id,
                    createNodeForAddAction(kind)
                )
            );
        },
        [node.id, onDraftChange, root]
    );

    const childTypeProperties = selectedType?.egenskapstyper ?? [];

    return (
        <>
            <div className="c-advanced-filter__node-header c-advanced-filter__node-header--compact">
                <div
                    className="c-advanced-filter__node-kind c-advanced-filter__node-kind--plain"
                    title="Filtrer på relasjon (foreldre/barn) for vegobjekter av en annen gitt type"
                >
                    <i
                        className="material-icons c-advanced-filter__node-kind-icon"
                        aria-hidden="true"
                    >
                        compare_arrows
                    </i>
                    <select
                        className="c-advanced-filter__input c-advanced-filter__input--inline c-advanced-filter__input--negation"
                        value={node.negated ? 'without' : 'with'}
                        onChange={handleNegationChange}
                        aria-label="Velg om du vil vise objekter med eller uten relasjon til valgt objekttype"
                        title="Velg med eller uten relasjon til valgt objekttype"
                    >
                        <option value="with">Relasjon</option>
                        <option value="without">Uten relasjon</option>
                    </select>
                </div>
                <select
                    className="c-advanced-filter__input c-advanced-filter__input--inline c-advanced-filter__input--relation-target"
                    value={node.targetTypeId?.toString() ?? ''}
                    onChange={handleTypeChange}
                    aria-label="Velg vegobjekttype for relasjon"
                    disabled={relationTargetOptions.length === 0}
                    title={selectedOption?.label ?? 'Velg relatert objekttype'}
                >
                    <option value="" disabled>
                        {relationTargetOptions.length > 0
                            ? 'Velg relatert objekttype'
                            : 'Ingen relaterte objekttyper'}
                    </option>
                    {relationTargetOptions.map(option => (
                        <option key={option.targetTypeId} value={option.targetTypeId}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {selectedType &&
                (node.child ? (
                    <div className="c-advanced-filter__nested c-advanced-filter__nested--relasjon">
                        <div className="c-advanced-filter__nested-header">
                            <div className="c-advanced-filter__nested-header-main">
                                <div
                                    className="c-advanced-filter__group-title"
                                    title={`Underfilter gjelder for ${selectedType.navn}`}
                                >
                                    <i
                                        className="material-icons c-advanced-filter__node-kind-icon"
                                        aria-hidden="true"
                                    >
                                        workspaces
                                    </i>
                                    <select
                                        className="c-advanced-filter__input c-advanced-filter__input--inline c-advanced-filter__input--negation"
                                        value={node.child.negated ? 'without' : 'with'}
                                        onChange={handleChildNegationChange}
                                        aria-label={`Velg om underfilteret for ${selectedType.navn} skal finne treff med eller uten det som er valgt i underfilteret`}
                                        title={`Velg med eller uten det som er valgt i underfilteret for ${selectedType.navn}`}
                                    >
                                        <option value="with">Underfilter</option>
                                        <option value="without">Underfilter uten</option>
                                    </select>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="c-advanced-filter__remove-button"
                                onClick={handleRemoveChild}
                            >
                                Fjern
                            </button>
                        </div>
                        <AdvancedFilterGroupEditor
                            root={root}
                            group={node.child}
                            groupDepth={childContainerDepth}
                            isRoot
                            egenskapstyper={childTypeProperties}
                            vegobjekttypeData={vegobjekttypeData}
                            datakatalogVegobjekttyper={datakatalogVegobjekttyper}
                            errorsByNodeId={errorsByNodeId}
                            onDraftChange={onDraftChange}
                            user={user}
                            disableSuggestedValueFetch={disableSuggestedValueFetch}
                        />
                    </div>
                ) : (
                    <AddButtons
                        onAdd={handleAddNestedNode}
                        containerDepth={childContainerDepth}
                    />
                ))}
        </>
    );
};
